// src/pages/VendorRegistration/VendorRegistration.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStore,
  faCogs,
  faListAlt,
  faExclamationTriangle,
  faHourglassHalf,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';

// Define the Vendor Registration options
const vendorRegistrationOptions = [
  {
    title: 'Merchants Approve',
    icon: faListAlt,
    path: '/shop/all-shops',
    perm: 'merchants.allShops',
    description: 'View and manage approved merchants'
  },
  {
    title: 'Merchant Commission',
    icon: faCogs,
    path: '/shop/commission-management',
    perm: 'merchants.commission',
    description: 'Set and change merchant commission'
  },
  {
    title: 'Merchants Rejected by bank',
    icon: faExclamationTriangle,
    path: '/shop/rejected-shops',
    perm: 'merchants.rejected',
    description: 'View and retry failed AceMoney merchant registrations'
  },
  {
    title: 'Merchants Pending by bank',
    icon: faHourglassHalf,
    path: '/shop/pending-onboarding',
    perm: 'merchants.pending',
    description: 'Merchants awaiting AceMoney bank approval'
  },
  {
    title: 'Merchants Advance Fund Manage',
    icon: faWallet,
    path: '/shop-report/wallet-report',
    perm: 'merchants.advanceFund',
    description: 'Manage merchant advance funds and wallet reports'
  }
];

const VendorRegistration = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = vendorRegistrationOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access vendor registration features. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 ,mt:-7}}>
        Vendor Registration
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ padding: 2 }}>
        {vendorRegistrationOptions.map((option) => (
          can(option.perm) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card
                elevation={3}
                sx={{
                  width: '100%',
                  maxWidth: 250,
                  height: 290,
                  borderRadius: 3,
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                >
                  <CardContent 
                    sx={{ 
                      width: '100%',
                      height: '100%',
                      textAlign: 'center', 
                      p: 2.5, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
                      <FontAwesomeIcon
                        icon={option.icon}
                        style={{ fontSize: '4.5rem' }}
                        className="fa-icon"
                        color="#FFA500"
                      />
                    </Box>
                    <Box sx={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', w: '100%' }}>
                      <Typography 
                        variant="h6" 
                        component="div" 
                        sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.3, textAlign: 'center' }}
                      >
                        {option.title}
                      </Typography>
                    </Box>
                    <Box sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', w: '100%' }}>
                      <Typography 
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.78rem', lineHeight: 1.25, textAlign: 'center' }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Box>
  );
};

export default VendorRegistration;